import moment from "moment";
import React, { useState } from "react";
import { BiCloudUpload, BiCopy, BiExport } from "react-icons/bi";
import { MdDelete } from "react-icons/md";
import Toggle from "react-toggle";
import DateTimePicker from "src/components/DateTimePicker/variant1";
import { useTooltip } from "src/hooks/useTooltip";
import { usePlan } from "src/modules/protocols/context";
import Inputfield from "../../../../../components/inputfield";
import { ProtocolType } from "../../../../fithub-shared/interfaces/protocol";
import "./styles.css";

function diasRestantes(dataFutura: Date): string {
  const dataAtual = moment();
  const dataFuturo = moment(dataFutura);
  const diferencaEmDias = dataFuturo.diff(dataAtual, "days");

  if (diferencaEmDias === 0) {
    const mins = dataFuturo.diff(dataAtual, "minutes");
    if (mins > 60) {
      const horas = dataFuturo.diff(dataAtual, "hours");
      return `${horas} hora${horas === 1 ? "" : "s"}`;
    } else {
      return mins <= 0 ? "Em Breve" : `${mins} minuto${mins > 1 ? "s" : ""}`;
    }
  } else {
    return `${diferencaEmDias} dia${diferencaEmDias > 1 ? "s" : ""}`;
  }
}

const convertDate = (value: Date | string | null | undefined) => {
  if (typeof value === "string") value = new Date(value);
  return value ?? null;
};

interface SetupProps {
  onChangeTitle: (t: string) => void;
  onChangeObs: (t: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onSaveTemplate: (id: string) => void;
  onExport: (id: string) => void;
}

const ProtocolSetup: React.FC<SetupProps> = (props) => {
  const { plan } = usePlan();
  const [current] = useState(plan.selected);
  const [enable, setEnable] = useState(convertDate(plan.selected?.enable));
  const def_active = plan.selected?.active;
  const [active, setActive] = useState(def_active);
  /*const [tooltip, setTooltip] = useState({ x: 0, y: 0, active: "" });

  function onMouseOver(e: React.MouseEvent<HTMLElement>) {
    setTooltip({
      x: e.currentTarget.offsetLeft + e.currentTarget.offsetWidth / 2 - 10,
      y: e.currentTarget.offsetTop - 10,
      active: e.currentTarget.getAttribute("aria-label") as string,
    });
  }

  function onMouseOut() {
    setTooltip({ x: 0, y: 0, active: "" });
  }*/

  const over = useTooltip({
    pointerPosition: true,
    offsetX: 5,
    offsetY: -35,
  });

  function handleToggle() {
    const status = !active;
    if (current === null) return;
    const nplan = { ...plan.selected } as ProtocolType;
    nplan.active = status;
    setActive(status);
    plan.update(nplan);
  }

  function handleDelete() {
    props.onDelete(plan.selected?.id ?? "");
  }

  function handleExport() {
    props.onExport(plan.selected?.id ?? "");
  }

  function handleDuplicate() {
    props.onDuplicate(plan.selected?.id ?? "");
  }

  function handleSaveTemplate() {
    props.onSaveTemplate(plan.selected?.id ?? "");
  }

  function onSetEnable(date: Date | null) {
    const nplan = { ...plan.selected } as ProtocolType;
    setEnable(date);
    nplan.enable = date?.toISOString() ?? null;

    if (active) {
      nplan.active = false;
      setActive(false);
    }

    if (nplan.disable && nplan.enable && nplan.disable < nplan.enable) {
      nplan.disable = null;
    }

    plan.update(nplan);
  }

  function onSetDisable(date: Date | null) {
    const nplan = { ...plan.selected } as ProtocolType;

    if (date !== null && nplan.enable) {
      nplan.disable =
        date < new Date(nplan.enable) ? nplan.enable : date.toISOString();
    } else {
      nplan.disable = date?.toISOString() ?? null;
    }

    plan.update(nplan);
  }

  return (
    <div className="flex flex-1 flex-col" id="hfcentered">
      {over.tooltipNode}
      <div className="config-box">
        <div className="config-line">
          <div className="w-full flex-1">
            <div className="flex w-full items-center gap-5">
              <Inputfield
                hint="Nome do Protocolo"
                inputProps={{
                  defaultValue: plan.selected?.title,
                  placeholder: "Nome do Protocolo",
                  style: {
                    fontSize: 20,
                    textDecoration: "underline",
                    textDecorationColor: "var(--zinc-200)",
                    textDecorationThickness: "2px",
                    textUnderlineOffset: "2px",
                    fontWeight: "bold",
                  },
                }}
                onEndText={props.onChangeTitle}
              />
              <div
                aria-label={`${
                  active ? "Desativar" : "Ativar"
                } visibilidade do plano para o aluno`}
                {...over.getTriggerProps()}
                className="mt-2 flex items-center gap-2"
              >
                <Toggle
                  aria-label="Salvar como Template"
                  checked={active}
                  onChange={handleToggle}
                  icons={false}
                />
              </div>
            </div>
          </div>
          <div className="w-full">
            <h5 className="ml-1 pt-1 text-2xs text-gray-500">
              Criado em{" "}
              {moment(plan.selected?.created_at).format("LL [às] HH:mm")}
            </h5>
          </div>
        </div>

        {/* Ativação */}
        <div
          className={`config-line ${active ? "pointer-events-none opacity-50" : ""}`}
        >
          <div className="flex flex-col">
            <h4>Data de Ativação:</h4>
            <p className="pl-1 text-xs text-gray-700">
              {!plan.selected?.enable || active ? (
                "Defina uma data para ATIVAR."
              ) : (
                <>
                  Protocolo ativará em
                  <strong className="ml-1">
                    {diasRestantes(new Date(plan.selected.enable))}
                  </strong>
                </>
              )}
            </p>
          </div>
          <DateTimePicker
            minDate={new Date()}
            defaultValue={enable}
            onChange={onSetEnable}
            placeholder="Data de Ativação"
          />
        </div>

        {/* Desativação */}
        <div
          className={`config-line ${active ? "pointer-events-none opacity-50" : ""}`}
        >
          <div className="flex flex-col">
            <h4>Data de Desativação:</h4>
            <p className="pl-1 text-xs text-gray-700">
              {!plan.selected?.disable ? (
                "Defina uma data para DESATIVAR."
              ) : (
                <>
                  Protocolo desativará em
                  <strong className="ml-1">
                    {diasRestantes(new Date(plan.selected.disable))}
                  </strong>
                </>
              )}
            </p>
          </div>
          <DateTimePicker
            minDate={
              convertDate(plan.selected?.disable) ?? enable ?? new Date()
            }
            defaultValue={convertDate(plan.selected?.disable)}
            onChange={onSetDisable}
            placeholder="Data de Desativação"
          />
        </div>

        {/* Ações */}
        <div className="config-line">
          <div className="flex gap-2">
            <button
              className="action-btn bg-red-500"
              aria-label="Deletar Plano"
              {...over.getTriggerProps()}
              onClick={handleDelete}
            >
              <MdDelete size={20} />
            </button>
            <button
              className="action-btn"
              aria-label="Salvar como Template"
              {...over.getTriggerProps()}
              onClick={handleSaveTemplate}
            >
              <BiCloudUpload size={20} />
            </button>
            <button
              className="action-btn"
              aria-label="Exportar para outro Aluno"
              {...over.getTriggerProps()}
              onClick={handleExport}
            >
              <BiExport size={20} />
            </button>
            <button
              className="action-btn"
              aria-label="Duplicar este Plano"
              {...over.getTriggerProps()}
              onClick={handleDuplicate}
            >
              <BiCopy size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Observações */}
      <div className="config-box mt-1 flex">
        <div>
          <p className="text-sm font-bold">OBSERVAÇÕES DO PROTOCOLO</p>
        </div>
        <textarea
          defaultValue={plan.selected?.obs}
          onBlur={(e) => props.onChangeObs(e.target.value)}
          placeholder="Escreva aqui..."
          maxLength={300}
          className="obs-textarea"
        />
      </div>
    </div>
  );
};

export default ProtocolSetup;
